import styled, { css } from "styled-components";
import { TCardVariant } from "./CondensedFeatureGrid";
import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";

const GAP = 20;
const CUSTOM_DESKTOP_BREAKPOINT_PX = "1160px";

export const RowsWrapper = styled.div`
  display: grid;
  row-gap: 16px;
  grid-template-columns: 1fr;
`;

export const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${GAP}px;

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    height: max-content;
  }
`;

export const CardWrap = styled.div<{
  $isOpen: boolean;
  $hasLessThanMaxItems: boolean;
}>`
  position: relative;
  transition: all 0.3s;
  flex: 1 1 0;
  min-width: 0;

  ${({ $hasLessThanMaxItems }) =>
    $hasLessThanMaxItems &&
    css`
      flex: 0 0 calc(25% - ${(16 / 1196) * 100}%);

      @media (max-width: ${CUSTOM_DESKTOP_BREAKPOINT_PX}) {
        flex: 0 0 calc(50% - ${(10 / 1196) * 100}%);
      }

      @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
        flex: 1 1 0;
      }
    `}

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      flex: 0 0 calc(50% - ${(10 / 1196) * 100}%);

      @media (max-width: ${CUSTOM_DESKTOP_BREAKPOINT_PX}) {
        flex: 0 0 calc(75% - ${(10 / 1196) * 100}%);
      }

      @media ${BREAKPOINTS.TABLET_AND_DOWN} {
        flex: 1;
      }
    `}

    @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    max-height: fit-content;
  }
`;

export const FeatureCardDisclaimer = styled.span`
  display: flex;
  position: absolute;
  left: 24px;
  bottom: 12px;
  color: #9fa5ad;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  z-index: 15;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    left: 12px;
    bottom: 8px;
  }
`;

export const CardTitle = styled.span<{
  $isActive: boolean;
  $isOtherLanguage: boolean;
}>`
  color: #3b3f45;
  font-size: 21px;
  font-weight: 700;
  line-height: normal;
  margin: 0;
  max-width: 238px;
  text-align: left;
  transition:
    max-width 0.3s,
    color 0.3s;
  text-wrap-style: pretty;
  text-overflow: ellipsis;
  overflow: hidden;
  z-index: 2;
  display: -webkit-box; /* stylelint-disable-line value-no-vendor-prefix */
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  ${({ $isOtherLanguage }) =>
    $isOtherLanguage &&
    css`
      word-break: break-word;
    `}

  ${({ $isActive }) =>
    $isActive &&
    css`
      br {
        display: none;
      }
    `}

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    max-width: 70%;
    font-size: 18px;
    text-wrap-style: pretty;

    br {
      display: none;
    }
  }
`;

export const CardDescription = styled.p<{ $isOverview?: boolean }>`
  color: #cdd1d5;
  font-size: 12px;
  font-weight: 400;
  left: -9999px;
  line-height: 16px;
  margin: 0;
  max-width: 180px;
  opacity: 0;
  position: absolute;
  text-align: left;
  width: 100%;
  text-wrap: pretty;

  ${({ $isOverview }) =>
    $isOverview &&
    css`
      max-width: 220px;
    `}

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    max-width: 100% !important;
  }
`;

export const VideoWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  aspect-ratio: 592 / 240;
  left: 0;
  top: 0;
  z-index: 1;
  transition: all 0.15s ease-in-out;
  transition-delay: 0.2s;

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    text-align: right;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    aspect-ratio: 1082 / 776;
    max-height: 0;
    transition: none;
  }
`;

export const VideoBackground = styled.video`
  height: 100%;
  object-fit: cover;
  object-position: center left;
  transition: none;
  width: 100%;
  opacity: 0;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    object-position: right center;
    max-width: 100%;
  }
`;

export const ImageBackground = styled.img`
  height: 100%;
  object-fit: cover;
  object-position: center left;
  transition: none;
  width: 100%;
  opacity: 0;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    object-position: right center;
    max-width: 100%;
  }
`;

export const ContentWrapper = styled.div<{
  $isSelectedModelsOnly?: boolean;
  $isComingSoon?: boolean;
}>`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  z-index: 1;
  gap: 12px;
  padding-right: 10px;
  position: relative;
  direction: ltr;

  svg {
    margin-left: -6px;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    height: auto;
    flex-direction: row;
    align-items: center;
    padding: 16px 36px 16px 16px;
    gap: 8px;

    svg {
      width: 40px;
      height: 40px;
    }

    ${({ $isSelectedModelsOnly, $isComingSoon }) =>
      ($isSelectedModelsOnly || $isComingSoon) &&
      css`
        padding-bottom: 36px;
      `}
  }

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    height: 100%;
  }
`;

export const ToggleBtn = styled.div<{
  $isActive: boolean;
  $variant: TCardVariant;
}>`
  display: flex;
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 5;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: #3b3f45;
  transition: background-color 0.3s;
  cursor: pointer;

  svg {
    margin-left: 1px;
    display: block !important;
    color: ${({ theme }) => theme.colors.white};
    transition: all 0.3s;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    top: 50%;
    transform: translateY(-50%) rotate(90deg);
  }

  ${({ $isActive, $variant, theme }) =>
    $isActive &&
    css`
      background-color: ${$variant === "dark" ? theme.colors.white : "#3B3F45"};

      svg {
        margin-right: 1px;
        margin-left: 0;
        color: ${$variant === "dark" ? "#3B3F45" : theme.colors.white};
        transform: rotate(180deg);
      }

      @media ${BREAKPOINTS.TABLET_AND_DOWN} {
        top: unset;
      }
    `}
`;

export const Card = styled.button<{ $isOpen: boolean; $variant: TCardVariant }>`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  border: none;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  height: 240px;
  max-height: 240px;
  outline: none;
  padding: 20px 12px 24px 24px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  width: 100%;
  z-index: 0;
  transition: max-height 0.5s;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    height: auto;
    padding: 0;
    min-height: 76px;
    max-height: 118px;
  }

  ${({ $isOpen, $variant, theme }) =>
    $isOpen &&
    css`
      @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
        background: ${$variant === "dark" ? "#020519" : theme.colors.white};
      }

      @media ${BREAKPOINTS.TABLET_AND_DOWN} {
        max-height: 520px;
      }

      svg {
        display: none;
      }

      ${CardTitle} {
        color: ${$variant === "dark" ? theme.colors.white : "#3B3F45"};
        font-size: 18px;
        max-width: 40%;
        text-overflow: unset;
        overflow: unset;
        display: block;
        -webkit-line-clamp: unset;
        -webkit-box-orient: unset;

        @media ${BREAKPOINTS.TABLET_AND_DOWN} {
          max-width: 100%;
        }
      }

      ${CardDescription} {
        color: ${$variant === "dark" ? "#CDD1D5" : "#676F79"};
        opacity: 1;
        position: initial;
        transition: opacity 0.7s;
        max-width: 40%;
      }

      ${VideoWrapper} {
        z-index: 0;

        video,
        img {
          opacity: 1;
        }

        @media ${BREAKPOINTS.TABLET_AND_DOWN} {
          max-height: 240px;
          position: relative;
        }
      }

      @media ${BREAKPOINTS.TABLET_AND_DOWN} {
        ${FeatureCardDisclaimer} {
          position: initial;
          padding: 0 16px 8px;
        }

        ${ContentWrapper} {
          flex-direction: column;
          align-items: flex-start;
          padding-bottom: 16px;
        }
      }
    `}
`;
